// apps/web/src/features/russia-requests/components/tabs/ChatTab.tsx
//
// RF drawer chat tab (#1067). Mirrors the China chat tab UX —
// thread + composer + "New" divider + IntersectionObserver auto-read
// + mark/unmark — but every wire call routes through the RF chat
// hooks introduced in #1066, so RF and China caches never cross-talk.
//
// @aiPurpose RF chat presentation. The backend's chat-party gate
//   (admin · authoring manager · acting logist) is authoritative on
//   every endpoint; this component does not impose extra client
//   gating because the drawer's permission guard already prevents
//   non-parties from opening the tab at all.
// @aiInvariant the first-unread divider snapshot is captured ONCE
//   per mount from the initial unread-summary value. Remounting (tab
//   close + reopen) re-snapshots from the now-authoritative server
//   state.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEventHandler,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Icon, Textarea } from '../../../../ui/index.js';
import { roleLabel } from '../../../../shared/index.js';
import { formatRelative } from '../../../../i18n/index.js';
import type { UserRef } from '../../../requests/display/index.js';
import {
  useRussiaRequestChatList,
  useRussiaRequestChatUnreadSummary,
  useMarkRussiaRequestChatRead,
  useMarkRussiaRequestChatUnread,
  usePostRussiaRequestChat,
  useUnmarkRussiaRequestChatUnread,
} from '../../queries.js';

export interface RussiaChatTabProps {
  requestId: string;
  me: UserRef;
}

// WHY (#1067): mirrors the China auto-read debounce — IntersectionObserver
// fires once per visible message; without coalescing this would emit one
// POST per message on a fast scroll. 400ms covers scroll-and-stop.
const AUTO_READ_DEBOUNCE_MS = 400;

export function ChatTab({ requestId, me }: RussiaChatTabProps) {
  const { t } = useTranslation('russiaRequests');
  const chatQuery = useRussiaRequestChatList(requestId, { limit: 100 });
  const postChatMutation = usePostRussiaRequestChat(requestId);
  const markRead = useMarkRussiaRequestChatRead(requestId);
  const markUnread = useMarkRussiaRequestChatUnread(requestId);
  const unmarkUnread = useUnmarkRussiaRequestChatUnread(requestId);
  const unreadSummaryQuery = useRussiaRequestChatUnreadSummary([requestId]);
  const summary = unreadSummaryQuery.data?.[0];
  const isMarkedUnread = summary?.isMarkedUnread ?? false;

  const [text, setText] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const didInitialScrollRef = useRef(false);

  const messages = useMemo(() => {
    const wireItems = chatQuery.data?.items;
    if (!wireItems) return [];
    return [...wireItems].reverse();
  }, [chatQuery.data?.items]);

  // INVARIANT (#1067 mirror of #570): the open-time snapshot is captured
  //   ONCE per mount. The "New" divider is positioned against this snapshot
  //   and MUST NOT move while the user reads.
  const initialUnreadRef = useRef<number | null>(null);
  if (initialUnreadRef.current === null && summary !== undefined) {
    initialUnreadRef.current = summary.unreadCount;
  }
  const initialUnreadCount = initialUnreadRef.current ?? 0;

  const firstUnreadMessageId = useMemo<string | null>(() => {
    if (initialUnreadCount <= 0) return null;
    let counter = 0;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i]!;
      if (m.by.id === me.id) continue;
      counter += 1;
      if (counter === initialUnreadCount) return m.id;
    }
    return null;
  }, [messages, initialUnreadCount, me.id]);

  // Initial scroll: jump to first-unread if present, else to bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (didInitialScrollRef.current) {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom <= 80) el.scrollTop = el.scrollHeight;
      return;
    }
    if (firstUnreadMessageId) {
      const target = el.querySelector<HTMLElement>(
        `[data-chat-message-id="${firstUnreadMessageId}"]`,
      );
      if (target) {
        target.scrollIntoView({ block: 'start', behavior: 'auto' });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    } else {
      el.scrollTop = el.scrollHeight;
    }
    didInitialScrollRef.current = true;
  }, [messages.length, firstUnreadMessageId]);

  // Debounced auto-read: track newest visible non-own message and POST
  // /chat/read once per debounce window. Gated by tab visibility so a
  // backgrounded tab does not advance the pointer.
  const pendingNewestRef = useRef<{ id: string; at: number } | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushRead = useCallback(() => {
    flushTimerRef.current = null;
    const pending = pendingNewestRef.current;
    if (!pending) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    pendingNewestRef.current = null;
    markRead.mutate({ messageId: pending.id });
  }, [markRead]);

  const scheduleRead = useCallback(
    (id: string, at: number) => {
      const prev = pendingNewestRef.current;
      if (!prev || at > prev.at) {
        pendingNewestRef.current = { id, at };
      }
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
      }
      flushTimerRef.current = setTimeout(flushRead, AUTO_READ_DEBOUNCE_MS);
    },
    [flushRead],
  );

  useEffect(() => {
    return () => {
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, []);

  const messageMeta = useMemo(() => {
    const m = new Map<string, { authorId: string; at: number }>();
    for (const msg of messages) m.set(msg.id, { authorId: msg.by.id, at: Date.parse(msg.at) });
    return m;
  }, [messages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = (entry.target as HTMLElement).dataset.chatMessageId;
          if (!id) continue;
          const meta = messageMeta.get(id);
          if (!meta) continue;
          if (meta.authorId === me.id) continue;
          scheduleRead(id, meta.at);
        }
      },
      { root: el, threshold: 0.5 },
    );
    const targets = el.querySelectorAll<HTMLElement>('[data-chat-message-id]');
    targets.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [messages.length, messageMeta, me.id, scheduleRead]);

  const send = () => {
    const trimmed = text.trim();
    if (!trimmed || postChatMutation.isPending) return;
    postChatMutation.mutate(trimmed, {
      onSuccess: () => setText(''),
    });
  };

  const onComposerKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    send();
  };

  const togglePending = markUnread.isPending || unmarkUnread.isPending;
  const onToggleMarkUnread = () => {
    if (togglePending) return;
    if (isMarkedUnread) {
      unmarkUnread.mutate();
    } else {
      markUnread.mutate();
    }
  };

  return (
    <div
      data-testid="russia-request-drawer-chat"
      style={{
        padding: '12px 24px 0',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingBottom: 8,
        }}
      >
        <button
          type="button"
          onClick={onToggleMarkUnread}
          disabled={togglePending}
          aria-pressed={isMarkedUnread}
          aria-label={isMarkedUnread ? t('chat.unmarkUnread') : t('chat.markUnread')}
          title={isMarkedUnread ? t('chat.unmarkUnread') : t('chat.markUnread')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 'var(--r-1)',
            border: '1px solid var(--border-1)',
            background: isMarkedUnread ? 'var(--status-error-bg)' : 'transparent',
            color: isMarkedUnread ? 'var(--status-error-fg)' : 'var(--fg-2)',
            fontSize: 'var(--fz-12)',
            cursor: togglePending ? 'wait' : 'pointer',
            opacity: togglePending ? 0.6 : 1,
          }}
        >
          <Icon name="circle-dot" size={13} />
          {isMarkedUnread ? t('chat.unmarkUnread') : t('chat.markUnread')}
        </button>
      </div>
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          paddingBottom: 12,
        }}
      >
        {!messages.length && (
          <div
            data-testid="russia-request-drawer-chat-empty"
            style={{
              padding: '48px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              color: 'var(--fg-3)',
            }}
          >
            <Icon name="message-square" size={24} style={{ color: 'var(--fg-4)' }} />
            <div style={{ fontSize: 13 }}>{t('chat.empty')}</div>
          </div>
        )}
        {messages.map((m) => {
          const isFirstUnread = firstUnreadMessageId === m.id;
          const isOwn = m.by.id === me.id;
          const at = Date.parse(m.at);
          return (
            <div key={m.id}>
              {isFirstUnread && (
                <div
                  data-testid="russia-chat-new-divider"
                  role="separator"
                  aria-label={t('chat.newDivider')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    margin: '6px 0',
                  }}
                >
                  <div style={{ flex: 1, height: 1, background: 'var(--status-error-fg)' }} />
                  <span
                    style={{
                      fontSize: 'var(--fz-11)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'var(--status-error-fg)',
                    }}
                  >
                    {t('chat.newDivider')}
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'var(--status-error-fg)' }} />
                </div>
              )}
              <div
                data-chat-message-id={m.id}
                style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}
              >
                <Avatar name={m.by.name} size={26} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg-1)' }}>
                      {m.by.name}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                      {roleLabel(m.by.role)}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                      · {formatRelative(at)}
                    </span>
                    {isOwn && <OwnCheckMark readByCounterparty={m.readByCounterparty === true} />}
                  </div>
                  <div
                    data-chat-message-text="true"
                    style={{
                      fontSize: 13,
                      color: 'var(--fg-1)',
                      marginTop: 3,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {m.text}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          borderTop: '1px solid var(--border-2)',
          padding: '12px 0',
          position: 'relative',
        }}
      >
        <div style={{ position: 'relative' }}>
          <Textarea
            value={text}
            onChange={setText}
            onKeyDown={onComposerKeyDown}
            rows={2}
            placeholder={t('chat.placeholder', { name: me.name })}
            ariaLabel={t('chat.placeholder', { name: me.name })}
            style={{
              paddingRight: 46,
              resize: 'none',
            }}
          />
          <button
            type="button"
            aria-label={t('chat.send')}
            title={t('chat.send')}
            disabled={!text.trim() || postChatMutation.isPending}
            onClick={send}
            style={{
              position: 'absolute',
              right: 7,
              bottom: 7,
              width: 30,
              height: 30,
              borderRadius: 'var(--r-1)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                text.trim() && !postChatMutation.isPending ? 'var(--accent-1)' : 'var(--bg-2)',
              color:
                text.trim() && !postChatMutation.isPending ? 'var(--fg-invert)' : 'var(--fg-3)',
              cursor: text.trim() && !postChatMutation.isPending ? 'pointer' : 'not-allowed',
              opacity: postChatMutation.isPending ? 0.6 : 1,
            }}
          >
            <Icon name="send-horizontal" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function OwnCheckMark({ readByCounterparty }: { readByCounterparty: boolean }): JSX.Element {
  const { t } = useTranslation('russiaRequests');
  const ariaLabel = readByCounterparty ? t('chat.aria.read') : t('chat.aria.sent');
  return (
    <span
      role="img"
      aria-label={ariaLabel}
      title={ariaLabel}
      data-testid={readByCounterparty ? 'russia-chat-check-double' : 'russia-chat-check-single'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        color: readByCounterparty ? 'var(--status-success-fg)' : 'var(--fg-3)',
      }}
    >
      <Icon name={readByCounterparty ? 'check-check' : 'check'} size={12} />
    </span>
  );
}
